import {Editor} from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import {TextBackground} from "@iserv2/flashcards-memo-sr/lib/tiptap/TextBackground";
import {BubbleLikeMenu} from "../tiptap/BubbleLikeMenu";

interface EditCardBubbleMenuButton {
    class: string,
    content: string,
    click: (editor: Editor) => any,
    isActive: (editor: Editor) => boolean
}

class EditCardBubbleMenu {
    buttons: EditCardBubbleMenuButton[] = [
        {class: "tippy-button--bold", content: `<span class="fa fa-bold"></span>`, click: (editor) => editor.chain().focus().toggleBold().run(), isActive: (editor) => editor.isActive('bold')},
        {class: "tippy-button--italic", content: `<span class="fa fa-italic"></span>`, click: (editor) => editor.chain().focus().toggleItalic().run(), isActive: (editor) => editor.isActive('italic')},
        {class: "tippy-button--underline", content: `<span class="fa fa-underline"></span>`, click: (editor) => editor.chain().focus().toggleUnderline().run(), isActive: (editor) => editor.isActive('underline')},
        {class: "tippy-button--subscript", content: `x<sub>2</sub>`, click: (editor) => editor.chain().focus().toggleSubscript().run(), isActive: (editor) => editor.isActive('subscript')},
        {class: "tippy-button--superscript", content: `x<sup>2</sup>`, click: (editor) => editor.chain().focus().toggleSuperscript().run(), isActive: (editor) => editor.isActive('superscript')},
        {class: "tippy-button--circle", content: `<span class="tippy-button--circle-red"></span>`, click: (editor) => editor.chain().focus().toggleTextBackground('red').run(), isActive: (editor) => editor.isActive('textBackground', {color: 'red'})},
        {class: "tippy-button--circle", content: `<span class="tippy-button--circle-blue"></span>`, click: (editor) => editor.chain().focus().toggleTextBackground('blue').run(), isActive: (editor) => editor.isActive('textBackground', {color: 'blue'})},
        {class: "tippy-button--circle", content: `<span class="tippy-button--circle-yellow"></span>`, click: (editor) => editor.chain().focus().toggleTextBackground('yellow').run(), isActive: (editor) => editor.isActive('textBackground', {color: 'yellow'})},
        {class: "tippy-button--circle", content: `<span class="tippy-button--circle-green"></span>`, click: (editor) => editor.chain().focus().toggleTextBackground('green').run(), isActive: (editor) => editor.isActive('textBackground', {color: 'green'})},
        {class: "", content: `<span class="fal fa-remove-format"></span>`, click: (editor) => editor.chain().focus().unsetAllMarks().run(), isActive: () => false},
    ]

    create(editor: Editor) {
        const menu = document.createElement("div")
        menu.innerHTML = this.buttons
            .map((button, index) => `<button type="button" class="tippy-button ${button.class}" data-menu-button="${index}">${button.content}</button>`)
            .join("");
        menu.addEventListener('click', (ev) => this.handleClick(ev, editor), /*{capture: true}*/)
        return menu;
    }

    getTargetButtonFromElement(element: HTMLElement) {
        const button = element.getAttribute('data-menu-button') || ''
        return /^\d+$/.test(button) ? this.buttons[parseInt(button)] : null
    }

    private getTargetButtonFromEvent(event: Event) {
        let path: any[] = [];
        if ('composedPath' in event) path = event.composedPath()
        if (!path && 'deepPath' in event) path = (event as any).deepPath

        const buttonElement = path
            .filter((el, index) => index <= 2)
            .find(el => el.tagName === 'BUTTON' && el.hasAttribute("data-menu-button"));
        return buttonElement ? this.getTargetButtonFromElement(buttonElement) : null;
    }

    handleClick(ev: Event, editor: Editor) {
        const button = this.getTargetButtonFromEvent(ev);
        if (button) button.click(editor)
    }

    update(content: HTMLElement, editor: Editor) {
        content.querySelectorAll('button').forEach(node => {
            const button = this.getTargetButtonFromElement(node);
            if (button) node.classList.toggle('is-active', button.isActive(editor))
        })
    }
}

const editCardBubbleMenu = new EditCardBubbleMenu();

const DefaultOptions = {insertBefore: false};

export function normalizeSideTextHtml(editor: Editor) {
    let html = editor.getHTML()

    // remove empty line from start and end
    return html
        .replace(/(<p><\/p>)+$/, "")
        .replace(/^(<p><\/p>)+/, "")
        .trim()
}

export function configureSideTextEditor(textarea: HTMLTextAreaElement, createOptions = DefaultOptions) {
    const createOptionsResolved = {
        ...DefaultOptions,
        ...createOptions
    }


    const textareaParent = textarea.parentElement
    if (!textareaParent) {
        return
    }

    const textareaName = textarea.getAttribute('name')
    const textareaHasError = textarea.classList.contains('is-invalid')
    const textareaHtmlFlag = textareaParent.querySelector(`input[name="${textareaName}H"]`) as HTMLInputElement
    if (!textareaHtmlFlag) {
        return
    }

    const editorContent = textareaHtmlFlag.value === 'false' ? textarea.innerHTML : textarea.value

    const editor: Editor = new Editor({
        element: textareaParent,
        content: editorContent,
        injectCSS: false,
        enableInputRules: false,
        enablePasteRules: false,
        extensions: [
            StarterKit, Underline, Subscript, Superscript, TextBackground,
            BubbleLikeMenu.configure({
                updateContent: (content) => editCardBubbleMenu.update(content, editor),
                createContent: () => editCardBubbleMenu.create(editor)
            }),
        ],
        onCreate({editor}) {
            // двигаем в начало
            if (textareaHasError || createOptionsResolved.insertBefore) {
                textarea.before(editor.view.dom)
            }
            if (textareaHasError) {
                editor.view.dom.classList.add('is-invalid')
            }
        },
        onUpdate({editor}) {
            textarea.value = normalizeSideTextHtml(editor)
            textareaHtmlFlag.value = 'true'
        },
    })
}